import { deepClone } from '@/_utils/deep-clone';
import { NewUndoRedoService } from '@/common/undo-redo/new-undo-redo.service';
import { createVehicleTransport } from '@/new-transport/_factory/vehicle-transport.factory';
import { Vehicle, VehicleTransport } from '@/new-transport/_models/transport.model';
import { VehicleSidenavHandleService } from '@/new-transport/_shared/vehicles-sidenav/vehicle-sidenav-handle.service';
import { VehiclesSidenavApiService } from '@/new-transport/_shared/vehicles-sidenav/vehicles-sidenav.api.service';
import { VehiclesTableComponent } from '@/new-transport/_shared/vehicles-sidenav/vehicles-table/vehicles-table.component';
import { TransportMapStore } from '@/new-transport/transport-map/_store/transport-map.store';
import { AsyncPipe } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	OnInit,
	inject,
	input,
	viewChild,
	Injector,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { MatTableModule } from '@angular/material/table';
import { getState } from '@ngrx/signals';
import {
	Observable,
	Subject,
	combineLatest,
	distinctUntilChanged,
	forkJoin,
	map,
	of,
	startWith,
	switchMap,
	takeUntil,
	tap,
} from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { OfferTransportKind } from '@/common/enums/OfferTransportKind';
import { InfoboxService } from '@/common/infobox/infobox.service';

@Component({
	selector: 'app-vehicles-sidenav',
	standalone: true,
	imports: [
		MatSidenav,
		MatInputModule,
		MatFormFieldModule,
		MatSidenavContainer,
		MatSidenavContent,
		MatButton,
		MatTableModule,
		MatAutocompleteModule,
		MatCheckboxModule,
		ReactiveFormsModule,
		AsyncPipe,
		VehiclesTableComponent,
	],
	templateUrl: './vehicles-sidenav.component.html',
	styleUrl: './vehicles-sidenav.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VehiclesSidenavComponent implements OnInit {
	filteredVehicles$: Observable<Vehicle[]>;
	realVehicles: Vehicle[] = [];
	virtualVehicles: Vehicle[] = [];
	planes: Vehicle[] = [];
	searchControl: FormControl<string | Vehicle> = new FormControl('');

	readonly tableColumns = ['vehicleName', 'vehicleCapacity', 'vehicleFreeSeats', 'vehicleCrew', 'vehicleActions'];

	readonly showOnlyVirtualVehicles = input.required<boolean>();
	readonly sidenav = viewChild<MatSidenav>('sidenav');

	readonly store = inject(TransportMapStore);
	private readonly apiService = inject(VehiclesSidenavApiService);
	private readonly sidenavHandle = inject(VehicleSidenavHandleService);
	private readonly cdr = inject(ChangeDetectorRef);
	private readonly destroy$ = new Subject<void>();
	private readonly undoRedoService = inject(NewUndoRedoService);
	private readonly injector = inject(Injector);
	private readonly infoboxService = inject(InfoboxService);

	private readonly transportKind$ = inject(ActivatedRoute).queryParams.pipe(
		map((params) => params?.transportKind ?? OfferTransportKind.COACH),
		distinctUntilChanged(),
	);

	private readonly vehiclesByTransportKind = this.transportKind$.pipe(
		switchMap((transportKind: OfferTransportKind) =>
			transportKind === OfferTransportKind.PLANE
				? this.apiService.getPlanes().pipe(tap((planes) => (this.planes = planes.results)))
				: forkJoin({
						realVehicles: this.apiService.getRealVehiclesByName(),
						virtualVehicles: this.apiService.getVirtualVehiclesByName(),
					}).pipe(
						tap(({ realVehicles, virtualVehicles }) => {
							this.realVehicles = realVehicles.results;
							this.virtualVehicles = virtualVehicles.results;
						}),
					),
		),
	);

	ngOnInit(): void {
		this.filteredVehicles$ = combineLatest([
			this.searchControl.valueChanges.pipe(startWith('')),
			toObservable(this.store.vehicles, { injector: this.injector }),
			this.vehiclesByTransportKind,
		]).pipe(switchMap(([searchValue]) => this._filter(searchValue || '')));

		this.sidenavHandle.toggle$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			void this.sidenav().toggle();
			this.cdr.detectChanges();
		});
	}

	displayFn(option: Vehicle): string {
		return option ? option.name : '';
	}

	selectVehicle(): void {
		const selectedVehicle = this.searchControl.value as Vehicle;
		if (!selectedVehicle) return;

		if (this.store.vehicles().some((v) => v.name === selectedVehicle.name)) {
			this.infoboxService.error('Ten pojazd został już wybrany');
			return;
		}

		const vehicle = createVehicleTransport(selectedVehicle);
		this.store.setVehicles(this.store.vehicles().concat(vehicle));

		this.useVehicle(vehicle);

		this.searchControl.patchValue(null);
		this.undoRedoService.setState(deepClone(getState(this.store)));
	}

	useVehicle($event: VehicleTransport): void {
		this.store.useVehicle($event);
	}

	unSelectVehicle($event: VehicleTransport): void {
		this.store.unselectVehicle($event);
		this.store.removeVehicleRelatedObjects($event.id);
		this.undoRedoService.setState(deepClone(getState(this.store)));
	}

	calculateVehicleFreeSeats($event: { vehicle: VehicleTransport; crew: number }): void {
		this.store.setVehicleFreeSeats($event);
		if ($event.vehicle.inUse) {
			const vehicleAfterUpdate = this.store.getVehicle($event.vehicle.id);
			this.useVehicle(vehicleAfterUpdate);
		}
	}

	private _filter(searchValue: string | Vehicle): Observable<Vehicle[]> {
		if (typeof searchValue !== 'string') return of([]);

		const filterValue = searchValue.toLowerCase();

		const vehicles: Vehicle[] =
			this.planes.length > 0
				? this.planes
				: this.showOnlyVirtualVehicles()
					? this.virtualVehicles
					: this.realVehicles;

		return of(
			vehicles.filter((vehicle) =>
				vehicle.name.trim().toLocaleLowerCase().includes(filterValue.toLocaleLowerCase()),
			),
		).pipe(
			map((vehicles) => this.sortByName(vehicles)),
			map((vehicles) => this.filterAlreadyAddedVehicles(vehicles)),
		);
	}

	private sortByName(vehicles: Vehicle[]): Vehicle[] {
		return vehicles.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
	}

	private filterAlreadyAddedVehicles(vehicles: Vehicle[]): Vehicle[] {
		return vehicles.filter((vehicle) => !this.store.vehicles().some((v) => v.name === vehicle.name));
	}
}
